"use client";

/**
 * Scrollback list of user and AI messages during a graph-driven intake turn.
 * Renders nothing when the history is empty.
 */

import type { ReactNode } from "react";
import { MarkdownProse } from "../data-display/MarkdownProse";
import { AiBubble } from "./AiBubble";
import { UserBubble } from "./UserBubble";

export interface ChatHistoryMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export interface IntakeChatHistoryProps {
  messages: ChatHistoryMessage[];
  /**
   * Render assistant prose as markdown (GFM tables, lists, emphasis, code)
   * instead of plain text. Agents that present ledgers or comparisons write
   * tables, which are unreadable as raw pipes.
   *
   * User messages are never markdown-rendered — user-authored text stays
   * literal.
   */
  markdown?: boolean;
  /** Optional content rendered immediately below an individual message. */
  renderMessageFooter?: (message: ChatHistoryMessage) => ReactNode;
}

export function IntakeChatHistory({
  messages,
  markdown = false,
  renderMessageFooter,
}: IntakeChatHistoryProps) {
  if (messages.length === 0) return null;
  return (
    <div className="mb-4 flex flex-col gap-4">
      {messages.map((message) => (
        <div key={message.id}>
          {message.role === "user" ? (
            <UserBubble>{message.content}</UserBubble>
          ) : (
            // Markdown turns get the full chat column: a four-column
            // assumptions table is unreadable inside the default 540px bubble.
            <AiBubble wide={markdown}>
              {markdown ? (
                <MarkdownProse>{message.content}</MarkdownProse>
              ) : (
                message.content
              )}
            </AiBubble>
          )}
          {renderMessageFooter?.(message)}
        </div>
      ))}
    </div>
  );
}
